import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NavbarComponent, IconComponent, FooterComponent, ViewToggleComponent } from '@/shared/components';
import { ContestService } from '@/services/contest.service';
import { Contest } from '@/models/contest.model';
import { ViewModeService } from '@/services';

@Component({
  selector: 'app-portal',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, IconComponent, NavbarComponent, FooterComponent, ViewToggleComponent],
  templateUrl: './portal.component.html',
})
export class PortalComponent implements OnInit {
  searchTerm = '';
  activeTab: 'FUTUROS' | 'EM_ANDAMENTO' | 'FINALIZADO' | 'INSCRICOES_ABERTAS' = 'INSCRICOES_ABERTAS';
  contests: Contest[] = [];
  isLoading = false;
  totalElements = 0;
  currentPage = 0;
  pageSize = 100;

  skeletons: number[] = Array(6).fill(0);

  constructor(private contestService: ContestService, private viewModeService: ViewModeService) {}

  ngOnInit() {
    this.loadContests();
  }

  loadContests() {
    this.isLoading = true;
    this.contests = [];

    this.contestService.getPublicContests({
      divisao: [this.activeTab],
      search: this.searchTerm || undefined,
      page: this.currentPage,
      size: this.pageSize,
      sortBy: 'dtPublicacao',
      sortDirection: 'DESC'
    }).subscribe({
      next: (response) => {
        this.contests = response.content;
        this.totalElements = response.totalElements;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading contests:', error);
        this.isLoading = false;
      }
    });
  }

  onSearch() {
    this.currentPage = 0;
    this.loadContests();
  }

  setActiveTab(tab: 'FUTUROS' | 'EM_ANDAMENTO' | 'FINALIZADO' | 'INSCRICOES_ABERTAS') {
    this.activeTab = tab;
    this.currentPage = 0; // Reset page when changing tab
    this.loadContests(); // Reload contests with new division
  }

  getConcursosByTab() {
    // No longer filtering on frontend - API returns filtered results
    return this.contests;
  }

  getStatusColor(tag: string): string {
    switch(tag) {
      case 'FUTURO':
        return 'bg-blue-500';
      case 'EM_ANDAMENTO':
        return 'bg-green-500';
      case 'FINALIZADO':
        return 'bg-[#F37835]';
      case 'INSCRICOES_ABERTAS':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  }

  getStatusLabel(tag: string): string {
    switch(tag) {
      case 'FUTURO':
        return 'Futuro';
      case 'EM_ANDAMENTO':
        return 'Em Andamento';
      case 'FINALIZADO':
        return 'Finalizado';
      case 'INSCRICOES_ABERTAS':
        return 'Inscrições Abertas';
      default:
        return 'Desconhecido';
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return '-';

    // Extrai diretamente da string ISO para evitar problemas de timezone
    // Formato esperado: "2026-01-15T14:30:00" ou "2026-01-15T14:30:00Z" ou "2026-01-15T14:30:00.000Z"
    const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (isoMatch) {
      const [, year, month, day, hours, minutes] = isoMatch;
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    
    // Fallback para Date se o formato não for ISO
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  /**
   * Extrai o código do tipo de etapa (pode ser string ou objeto)
   */
  private extractTipoCodigo(tipo: string | any): string {
    if (typeof tipo === 'object' && tipo !== null) {
      return tipo.codigo || tipo.nome || '';
    }
    return tipo || '';
  }

  getInscriptionStage(contest: Contest) {
    return contest.etapas.find(etapa => this.extractTipoCodigo(etapa.tipo) === 'INSCRICAO');
  }

  getExamStage(contest: Contest) {
    return contest.etapas.find(etapa => {
      const codigo = this.extractTipoCodigo(etapa.tipo);
      return codigo === 'PROVA_OBJETIVA' || codigo === 'PROVA';
    });
  }

  getPaymentDeadlineStage(contest: Contest) {
    return contest.etapas.find(etapa => this.extractTipoCodigo(etapa.tipo) === 'PRAZO_PAGAMENTO');
  }

  get viewMode() {
    return this.viewModeService.viewMode();
  }
}
